import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from './components/header/header.component';
import { UploadPanelComponent } from './shared/upload-panel/upload-panel.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { Observable, Subject } from 'rxjs';
import { SidebarService } from './services/sidebar-btn/sidebar.service';
import { UploadService } from './services/upload/upload.service';
import { VersionService } from './services/version/version.service';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    HeaderComponent,
    UploadPanelComponent,
    SidebarComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'my-angular-app';
  isOpen$: Observable<boolean>;

  // Whether to run version checks. Off in production for now (per-environment). The update notice
  // itself is rendered in the header's account menu (see HeaderComponent).
  readonly versionDetectionEnabled = environment.versionDetectionEnabled;

  private destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
    private renderer: Renderer2,
    private sidebarService: SidebarService,
    private uploadService: UploadService,
    private versionService: VersionService
  ) {
    this.isOpen$ = this.sidebarService.isOpen$;
  }

  ngOnInit() {
    this.uploadService.resumeInterruptedUploads();

    // Where enabled, poll for new deploys so the header can surface an update notice. We never
    // force a reload — the user chooses when to refresh.
    if (this.versionDetectionEnabled) {
      this.versionService.startPolling();
    }

    this.sidebarService.isOpen$.pipe(takeUntil(this.destroy$)).subscribe((isOpen) => {
      if (isOpen) {
        this.renderer.addClass(document.body, 'sidebar-is-open');
      } else {
        this.renderer.removeClass(document.body, 'sidebar-is-open');
      }
    });
  }

  ngOnDestroy(): void {
    this.versionService.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Most pages have a header and sidebar, which require a logged-in user to fetch data. Don't show for login/signup etc
  showAppShell(): boolean {
    const standaloneLayoutPages = ['/login', '/signup', '/forgot-password', '/license-agreement'];
    if (standaloneLayoutPages.includes(this.router.url)) {
      return false;
    } else {
      return true;
    }
  }
}
